import type { CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Footer } from '@components/Layout'
import { Seo } from '@components/Seo'
import { useLang, withLang } from '@shared/i18n/useLang'
import { linkify } from '@shared/lib/linkify'

interface Requisite { label: string; value: string }
interface PurposeCard { title: string; data: string; legal: string; types: string }

interface PolitikaData {
  pageTitle: string
  revisionDate: string
  revision: string
  s1: { text: string; requisitesLabel: string; requisites: Requisite[]; text2: string; text3: string }
  s2: { title: string; items: string[] }
  s3: { title: string; rightsIntro: string; rights: string[]; dutiesIntro: string; duties: string[] }
  s4: { title: string; rightsIntro: string; rights: string[]; dutiesIntro: string; duties: string[]; note: string }
  s5: { title: string; items: string[] }
  s6: { title: string; labels: { data: string; legal: string; types: string }; cards: PurposeCard[] }
  s7: { title: string; items: string[] }
  s8: { title: string; intro: string; items: string[] }
  s9: { title: string; items: string[] }
  s10: { title: string; items: string[] }
  s11: { title: string; text: string }
  s12: { title: string; items: string[] }
}

const paraStyle: CSSProperties = { margin: 0, fontSize: 14.5, lineHeight: 1.75, color: '#3A434D' }
const bulletStyle: CSSProperties = { ...paraStyle, margin: '0 0 0 20px' }
const h2Style: CSSProperties = { margin: 0, fontSize: 20, fontWeight: 700, color: '#171C22' }
const sectionColStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 12 }

function P({ children, style }: { children: string; style?: CSSProperties }) {
  return <p style={style ?? paraStyle}>{linkify(children)}</p>
}

export function PolitikaPage() {
  const { t } = useTranslation()
  const lang = useLang()
  const data = t('politika', { returnObjects: true }) as PolitikaData

  return (
    <>
      <Seo title={t('seo.politika.title')} description={t('seo.politika.description')} />

      {/* Page head */}
      <section className="section-hero-bg" style={{ padding: '56px 0' }}>
        <div className="container-page" style={{ maxWidth: 900, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase' }}>
            <Link to={withLang(lang, '/')} style={{ color: '#5C6670', transition: 'color .15s' }}>{t('common.home')}</Link>
            <span style={{ color: '#3A434D' }}>/</span>
            <span style={{ color: '#93A3AE' }}>{t('common.politika')}</span>
          </div>
          <h1 className="h1-page" style={{ margin: 0, fontSize: 38, fontWeight: 750, letterSpacing: '-.015em', color: '#FFFFFF' }}>
            {data.pageTitle}
          </h1>
          <p style={{ margin: 0, fontSize: 14, color: '#93A3AE' }}>
            {t('politika.revision', { date: data.revisionDate })}
          </p>
        </div>
      </section>

      {/* Текст политики */}
      <section style={{ padding: '64px 0 96px' }}>
        <div className="container-page" style={{ maxWidth: 900, display: 'flex', flexDirection: 'column', gap: 44 }}>

          {/* 1. Общие положения */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{t('politika.s1.title', { defaultValue: '1. Общие положения' })}</h2>
            <P>{data.s1.text}</P>
            <P>{data.s1.requisitesLabel}</P>
            <div className="card" style={{ overflow: 'hidden' }}>
              {data.s1.requisites.map((r, i) => (
                <div
                  key={r.label}
                  style={{
                    display: 'grid', gridTemplateColumns: '220px 1fr',
                    borderBottom: i === data.s1.requisites.length - 1 ? 'none' : '1px solid #D9DEE2',
                  }}
                >
                  <span style={{ padding: '14px 18px', fontSize: 13, color: '#5C6670', borderRight: '1px solid #D9DEE2' }}>{r.label}</span>
                  <span style={{ padding: '14px 18px', fontSize: 14, color: '#171C22' }}>{linkify(r.value)}</span>
                </div>
              ))}
            </div>
            <P>{data.s1.text2}</P>
            <P>{data.s1.text3}</P>
          </div>

          {/* 2. Термины */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s2.title}</h2>
            {data.s2.items.map(item => <P key={item}>{item}</P>)}
          </div>

          {/* 3. Права и обязанности Оператора */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s3.title}</h2>
            <P>{data.s3.rightsIntro}</P>
            {data.s3.rights.map(item => <P key={item} style={bulletStyle}>{`— ${item}`}</P>)}
            <P>{data.s3.dutiesIntro}</P>
            {data.s3.duties.map(item => <P key={item} style={bulletStyle}>{`— ${item}`}</P>)}
          </div>

          {/* 4. Права и обязанности субъектов ПДн */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s4.title}</h2>
            <P>{data.s4.rightsIntro}</P>
            {data.s4.rights.map(item => <P key={item} style={bulletStyle}>{`— ${item}`}</P>)}
            <P>{data.s4.dutiesIntro}</P>
            {data.s4.duties.map(item => <P key={item} style={bulletStyle}>{`— ${item}`}</P>)}
            <P>{data.s4.note}</P>
          </div>

          {/* 5. Принципы обработки */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s5.title}</h2>
            {data.s5.items.map(item => <P key={item}>{item}</P>)}
          </div>

          {/* 6. Цели обработки */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <h2 style={h2Style}>{data.s6.title}</h2>
            {data.s6.cards.map(card => (
              <div key={card.title} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span style={{ fontSize: 15, fontWeight: 650, color: '#171C22' }}>{card.title}</span>
                <div className="card" style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#5C6670' }}>{data.s6.labels.data}</span>
                    <span style={{ fontSize: 14.5, lineHeight: 1.65, color: '#3A434D' }}>{card.data}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#5C6670' }}>{data.s6.labels.legal}</span>
                    <span style={{ fontSize: 14.5, lineHeight: 1.65, color: '#3A434D' }}>{card.legal}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#5C6670' }}>{data.s6.labels.types}</span>
                    <span style={{ fontSize: 14.5, lineHeight: 1.65, color: '#3A434D' }}>{card.types}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 7. Условия обработки */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s7.title}</h2>
            {data.s7.items.map(item => <P key={item}>{item}</P>)}
          </div>

          {/* 8. Порядок сбора, хранения, передачи */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s8.title}</h2>
            <P>{data.s8.intro}</P>
            {data.s8.items.map(item => <P key={item}>{item}</P>)}
          </div>

          {/* 9. Перечень действий */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s9.title}</h2>
            {data.s9.items.map(item => <P key={item}>{item}</P>)}
          </div>

          {/* 10. Трансграничная передача */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s10.title}</h2>
            {data.s10.items.map(item => <P key={item}>{item}</P>)}
          </div>

          {/* 11. Конфиденциальность */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s11.title}</h2>
            <P>{data.s11.text}</P>
          </div>

          {/* 12. Заключительные положения */}
          <div style={sectionColStyle}>
            <h2 style={h2Style}>{data.s12.title}</h2>
            {data.s12.items.map(item => <P key={item}>{item}</P>)}
          </div>

        </div>
      </section>

      <Footer />
    </>
  )
}
